
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ContactForm from '../../components/base/ContactForm';
import TestimonialsSlider from '../../components/base/TestimonialsSlider';

const TableViewPage: React.FC = () => {
  useEffect(() => {
    // Add Schema.org JSON-LD for Table View page
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Table View Painters",
      "description": "Professional painting contractors in Table View, Western Cape. Expert interior, exterior & roof painting services for coastal residential and commercial properties.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/table-view-painters`,
      "telephone": "",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Table View",
        "addressRegion": "Western Cape",
        "addressCountry": "ZA"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "-33.8116",
        "longitude": "18.4906"
      },
      "serviceArea": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": "-33.8116",
          "longitude": "18.4906"
        },
        "geoRadius": "5000"
      },
      "services": [
        "Interior Painting Table View",
        "Exterior Painting Table View", 
        "Roof Painting Table View",
        "Commercial Painting Table View",
        "Coastal Property Painting Table View"
      ],
      "areaServed": "Table View, Western Cape, South Africa"
    });
    document.head.appendChild(script);

    // Update page title and meta
    document.title = "Table View Painters - Professional Coastal Painting Contractors Western Cape | Get Quote";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional painting contractors in Table View, Western Cape. Expert coastal property painting with Table Mountain views. Specialized marine-grade coatings and weather protection. Get your free quote today.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Table View painters, coastal painting contractors, house painters Western Cape, marine painting, Table Mountain views');
    }

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const services = [
    {
      icon: 'ri-home-4-line',
      title: 'Interior Painting',
      description: 'Transform your Table View home with professional interior painting that complements ocean views.',
      link: '/interior-painting'
    },
    {
      icon: 'ri-building-line',
      title: 'Exterior Painting',
      description: 'Protect your Table View property with marine-grade exterior painting solutions.',
      link: '/exterior-painting'
    },
    {
      icon: 'ri-home-2-line',
      title: 'Roof Painting',
      description: 'Specialized roof painting for Table View\'s coastal environment with mountain backdrop.',
      link: '/roof-painting'
    },
    {
      icon: 'ri-office-building-line',
      title: 'Commercial Painting',
      description: 'Professional commercial painting services for Table View businesses and developments.',
      link: '/commercial-painting'
    },
  ];

  const whyChooseUs = [
    {
      icon: 'ri-landscape-line',
      title: 'Scenic Location Expertise',
      description: 'Our Table View contractors understand painting for properties with stunning mountain and ocean views.',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Coastal Protection',
      description: 'Advanced marine coatings designed specifically for Table View\'s coastal environment.',
    },
    {
      icon: 'ri-time-line',
      title: 'Reliable Service',
      description: 'Dependable response times for all Table View painting projects and maintenance needs.',
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Fair Pricing',
      description: 'Competitive rates for premium coastal painting services in Table View.',
    },
  ];

  const tableViewFeatures = [
    {
      icon: 'ri-mountain-line',
      title: 'Table Mountain Views',
      description: 'Color schemes and finishes that complement Table View\'s iconic mountain backdrop.'
    },
    {
      icon: 'ri-water-percent-line',
      title: 'Ocean Breeze Protection',
      description: 'Salt-resistant coatings designed for Table View\'s constant ocean breeze exposure.'
    },
    {
      icon: 'ri-sun-line',
      title: 'Sunset Orientation',
      description: 'UV protection systems perfect for Table View\'s west-facing sunset exposure.'
    },
    {
      icon: 'ri-building-2-line',
      title: 'Modern Development',
      description: 'Contemporary painting solutions for Table View\'s growing residential developments.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://public.readdy.ai/ai/img_res/d0401465c03d1af009be627ca8ef24a0.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6">
            <span className="block">Table View</span>
            <span className="block text-[#e9f638]">PAINTERS</span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
            Professional coastal painting contractors serving Table View and surrounding areas.
          </p>
          <p className="text-lg md:text-xl mb-12 max-w-4xl mx-auto opacity-90">
            Connect with Table View's premier coastal painting specialists. We deliver exceptional 
            results for properties with stunning Table Mountain and ocean views.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="text-black px-8 py-4 rounded-full text-lg font-semibold transition-colors duration-200 whitespace-nowrap cursor-pointer"
              style={{ backgroundColor: '#e9f638' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d4e532'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#e9f638'}
            >
              Get Free Table View Quote
            </Link>
            <a 
              href="#services" 
              className="border-2 border-white text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white hover:text-black transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              View Services
            </a>
          </div>
        </div>
      </section>

      {/* About Table View Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Exceptional Painting Services in Table View
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Table View, renowned for its spectacular sunsets over the Atlantic Ocean and 
                unobstructed views of Table Mountain, offers some of the Western Cape's most 
                desirable coastal living. This prime location requires painting services that 
                not only protect against the marine environment but also enhance the natural 
                beauty of this scenic area.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                From the modern residential complexes along the beachfront to the established 
                neighborhoods inland, our Table View painting contractors understand the unique 
                requirements of coastal properties. We provide comprehensive painting solutions 
                using advanced marine-grade materials that withstand salt air, ocean winds, 
                and intense UV exposure.
              </p>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Table View's Scenic Beauty and Specialized Painting Needs
              </h3>
              <p className="text-lg text-gray-600">
                The combination of ocean proximity and mountain views makes Table View properties 
                unique. Our contractors specialize in color selection and coating systems that 
                complement the natural landscape while providing maximum protection against 
                the coastal elements that can quickly deteriorate inferior paint systems.
              </p>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Beautiful%20coastal%20homes%20in%20Table%20View%20South%20Africa%20with%20Table%20Mountain%20and%20ocean%20views%2C%20professional%20painting%20work%20visible%20on%20beachfront%20properties%2C%20stunning%20sunset%20lighting%20and%20scenic%20coastal%20landscape&width=600&height=400&seq=table-view-coastal-homes&orientation=landscape"
                alt="Table View coastal properties with professional painting services"
                className="rounded-lg shadow-lg w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Professional Coastal Painting Services in Table View
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our Table View painting contractors provide specialized coastal painting solutions for all property types
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <Link 
                key={index} 
                to={service.link}
                className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200 border border-gray-100 group cursor-pointer"
              >
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: '#e9f638' }}>
                  <i className={`${service.icon} text-2xl text-black`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-[#e9f638] transition-colors duration-200">
                  {service.title}
                </h3>
                <p className="text-gray-600">{service.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Table View Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Specialized Solutions for Table View Properties
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Painting services designed specifically for Table View's unique coastal and mountain environment
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {tableViewFeatures.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#e9f638' }}>
                  <i className={`${feature.icon} text-2xl text-black`}></i>
                </div>
                <h4 className="text-xl font-semibold text-gray-900 mb-4">{feature.title}</h4>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Table View Specific Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Table View Painting Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Tailored painting services for every type of Table View property and coastal environment
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h4 className="text-xl font-semibold text-gray-900 mb-4">
                Table View Beachfront Property Painting
              </h4>
              <p className="text-gray-600 mb-4">
                Specialized painting services for Table View's prestigious beachfront properties. 
                Our contractors use the most advanced marine coatings available to protect against 
                constant salt spray, ocean winds, and the intense UV radiation from both direct 
                sunlight and reflection off the ocean surface.
              </p>
              <ul className="text-gray-600 space-y-2">
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  Advanced marine coatings
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  Salt spray protection
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  UV reflection resistance
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h4 className="text-xl font-semibold text-gray-900 mb-4">
                Table View Sunset-Facing Property Painting
              </h4>
              <p className="text-gray-600 mb-4">
                Table View's west-facing orientation means properties receive intense afternoon 
                and evening sun exposure. Our contractors use specialized UV-resistant formulations 
                and heat-reflective coatings that maintain their appearance and protective 
                properties despite constant exposure to the setting sun.
              </p>
              <ul className="text-gray-600 space-y-2">
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  Heat-reflective coatings
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  UV-resistant formulations
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  Color stability guarantee
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg">
              <h4 className="text-xl font-semibold text-gray-900 mb-4">
                Table View Residential Complex Painting
              </h4>
              <p className="text-gray-600 mb-4">
                Table View features numerous modern residential complexes and apartment buildings 
                that require coordinated painting approaches. We work with body corporates and 
                property managers to deliver consistent, high-quality results that enhance 
                property values and resident satisfaction.
              </p>
              <ul className="text-gray-600 space-y-2">
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  Body corporate coordination
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  Consistent quality standards
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line text-[#e9f638] mr-2"></i>
                  Property value enhancement
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Table View Painting Specialists
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Coastal expertise and scenic location understanding for all Table View painting projects
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#e9f638' }}>
                  <i className={`${item.icon} text-2xl text-black`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Table View Areas Covered */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Table View Areas We Serve
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive coastal painting services throughout Table View and surrounding neighborhoods
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h5 className="text-lg font-semibold text-gray-900 mb-3">Table View Beachfront</h5>
              <p className="text-gray-600">
                Prime oceanfront properties with direct beach access requiring the highest grade 
                marine coatings and specialized painting techniques for maximum coastal protection.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h5 className="text-lg font-semibold text-gray-900 mb-3">Table View Central</h5>
              <p className="text-gray-600">
                Established residential areas with mix of older homes and modern developments, 
                each requiring tailored painting approaches based on architectural style and exposure.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h5 className="text-lg font-semibold text-gray-900 mb-3">Table View Heights</h5>
              <p className="text-gray-600">
                Elevated properties with enhanced mountain and ocean views, benefiting from 
                specialized color schemes that complement the spectacular natural surroundings.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h5 className="text-lg font-semibold text-gray-900 mb-3">Table View Marina</h5>
              <p className="text-gray-600">
                Properties near the marina and harbor areas requiring industrial-grade marine 
                coatings designed for the harshest coastal conditions and salt exposure.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h5 className="text-lg font-semibold text-gray-900 mb-3">Table View Residential Estates</h5>
              <p className="text-gray-600">
                Modern residential complexes and gated communities requiring coordinated painting 
                programs that maintain consistent aesthetic standards and property values.
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h6 className="text-lg font-semibold text-gray-900 mb-3">Greater Table View Area</h6>
              <p className="text-gray-600">
                Extended service coverage including nearby coastal developments and inland 
                properties that benefit from our specialized coastal painting expertise and techniques.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Related Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore our complete range of professional painting services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link 
              to="/interior-painting"
              className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200 text-center group cursor-pointer"
            >
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-home-4-line text-2xl text-black"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 group-hover:text-[#e9f638] transition-colors duration-200">
                Interior Painting
              </h3>
            </Link>

            <Link 
              to="/exterior-painting"
              className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200 text-center group cursor-pointer"
            >
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-building-line text-2xl text-black"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 group-hover:text-[#e9f638] transition-colors duration-200">
                Exterior Painting
              </h3>
            </Link>

            <Link 
              to="/roof-painting"
              className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200 text-center group cursor-pointer"
            >
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-home-2-line text-2xl text-black"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 group-hover:text-[#e9f638] transition-colors duration-200">
                Roof Painting
              </h3>
            </Link>

            <Link 
              to="/commercial-painting"
              className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-200 text-center group cursor-pointer"
            >
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#e9f638' }}>
                <i className="ri-office-building-line text-2xl text-black"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 group-hover:text-[#e9f638] transition-colors duration-200">
                Commercial Painting
              </h3>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Table View Customers Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from satisfied customers throughout Table View's coastal communities
            </p>
          </div>
          <TestimonialsSlider />
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Get Your Free Table View Painting Quote
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ready to enhance your Table View coastal property? Connect with professional painting specialists today
            </p>
          </div>
          <ContactForm />
        </div>
      </section>

      <Footer />
      
      {/* Elfsight AI Chatbot */}
      <div>
        <script src="https://elfsightcdn.com/platform.js" async></script>
        <div className="elfsight-app-bd0c8f1c-533d-413e-bee7-63e0ccb9fdbf" data-elfsight-app-lazy></div>
      </div>
    </div>
  );
};

export default TableViewPage;
